import { computed, DestroyRef, effect, inject, Signal, signal, untracked } from '@angular/core';
import { httpResource, HttpResourceRef } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AnalyticsService,
  OpenCaseAbout,
  OpenCasesService,
  UserService,
  VideoApiService,
  VideoHubService,
} from '@core/services';
import { ChatConversations, Conversation } from '@shared/components/chat/chat-conversations';
import { AvailabilityStatus } from '@shared/enums';
import { PermissionService } from '@core/permissions';
import { MibBreakpointService, timeToDuration } from '@medinbox/angular';
import { DEFAULT_ZONES, REMEMBERED_ENTRIES, STORAGE_KEYS } from '@shared/constants';
import { PublicationStatus } from '@shared/enums/publication-status.enum';
import {
  AnalyticsEvents,
  CaseZone,
  ContentGenerationStatus,
  LiveReport,
  Pii,
  PiiList,
  TabsState,
  Video,
  VideoCut,
  VideoLiveStream,
} from '@shared/models';
import { ZoneRow } from '@shared/utils';
import { environment } from 'src/environments/environment';
import { finalize } from 'rxjs';
import { ContentService } from './content.service';
import { caseZoneRules } from './zones-rules';
import { VideoPlayerService } from './playback/video-player.service';

/* `rounded-mib-lg` (6px) is the lib's largest radius. */
export const TAB_COMMON_CLASSES = 'h-full rounded-mib-lg not-expanded:rounded-none overflow-hidden';
export const TAB_HEADER_CLASSES =
  'h-17.5 not-height-medium:h-12 w-full flex items-center justify-between shrink-0';

/** What the bar keeps of a case beside its name, so a reload can draw its tab and its bar. */
function aboutTab(video: Video): OpenCaseAbout {
  return { createdAt: video.createdAt, publicationStatus: video.publicationStatus };
}

/**
 * A push is built from the entity alone, so signed URLs, per-user flags and counts come back at
 * their defaults — merging it wholesale would drop the media source and the PHI findings.
 */
function mergePush(current: Video, pushed: Video): Video {
  return {
    ...current,
    videoStatus: pushed.videoStatus,
    processedS3Key: pushed.processedS3Key,
    title: pushed.title,
    durationSeconds: pushed.durationSeconds,
    updatedAt: pushed.updatedAt,
    thumbnailUrl: pushed.thumbnailUrl,
    uploaded: pushed.uploaded,
    startRecordTimestamp: pushed.startRecordTimestamp,
    endRecordTimestamp: pushed.endRecordTimestamp,
    summary: pushed.summary,
    chatStatus: pushed.chatStatus,
    mediaStatus: pushed.mediaStatus,
    publicationStatus: pushed.publicationStatus,
    chunksMergeRequestStatus: pushed.chunksMergeRequestStatus,
  };
}

/**
 * One case held open: its record, its generated contents, its findings and the layout it is shown
 * in. It lives as long as the case's tab, whichever tab is on screen, so switching tabs costs
 * nothing and two cases never share a panel, a cut or a message.
 *
 * Built by {@link CaseSessionsService} in an injector of its own: what it fetches and watches
 * ends with that injector.
 */
export class CaseSession {
  private readonly analytics = inject(AnalyticsService);
  private readonly permissions = inject(PermissionService);
  private readonly videoApi = inject(VideoApiService);
  private readonly contentService = inject(ContentService);
  private readonly videoHub = inject(VideoHubService);
  private readonly openCases = inject(OpenCasesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly videoProgress = inject(VideoPlayerService);
  private readonly conversations = inject(ChatConversations);
  private readonly users = inject(UserService);

  /** An unpublished shared case answers with an empty body: a `null` value. */
  readonly videoResource: HttpResourceRef<Video | null | undefined> = httpResource<Video | null>(
    () => `${environment.apiUrl}/v1/videos/${this.id}`,
  );
  readonly video = computed(() =>
    this.videoResource.hasValue() ? (this.videoResource.value() ?? undefined) : undefined,
  );
  readonly videoState: Signal<'ready' | 'loading' | 'error' | 'waiting'> = computed(() => {
    if (this.videoResource.error()) {
      return 'error';
    }
    // `isLoading()` is also true while the resource reloads, its value still on screen: asking it
    // first made the template tear the zones down and rebuild them on every update.
    if (this.video()) {
      return 'ready';
    }
    if (this.videoResource.isLoading()) {
      return 'loading';
    }
    // The case exists but has nothing to play yet: it is still being processed.
    return 'waiting';
  });
  readonly title = computed(() => this.video()?.title || '');

  /**
   * Whether the case is being recorded live right now.
   *
   * Read from `isLive`, which the API resolves from the room's sessions. Not from
   * `chunksMergeRequestStatus`: it stays `waiting` until the first live merge and goes through
   * `merging` on every one after, so it would hide the stream and the stop for most of a live.
   * A push does not carry the flag, so the case is read again to learn that a live has ended —
   * see {@link applyLiveUpdate} and {@link liveEnded}.
   */
  readonly liveInProgress = computed(() => this.video()?.isLive === true);
  /**
   * The credentials to watch the live, asked for only while one runs.
   *
   * Kept apart from the case read because a token is signed on every request: renewing it must not
   * reload the case, and a case that is not live must not cost the request at all. The API answers
   * `204` — a `null` value — when no session feeds the case any more.
   */
  private readonly liveResource: HttpResourceRef<VideoLiveStream | null | undefined> =
    httpResource<VideoLiveStream | null>(() =>
      this.liveInProgress()
        ? `${environment.apiUrl}/v1/videos/${this.id}/live-subscription`
        : undefined,
    );
  /** What the live player joins with, `null` until it is in or once there is nothing to watch. */
  readonly live = computed(() =>
    this.liveResource.hasValue() ? (this.liveResource.value() ?? null) : null,
  );

  /* The case's own id, not `this.id` (a guest's share code): a push keeping it reads nothing again. */
  private readonly reportVideoId = computed(() =>
    this.liveInProgress() ? undefined : this.video()?.id,
  );
  /** The recorded report, read once the case is known not to be live. `204` is a `null` value. */
  private readonly reportResource: HttpResourceRef<LiveReport | null | undefined> =
    httpResource<LiveReport | null>(() => {
      const videoId = this.reportVideoId();
      return videoId ? `${environment.apiUrl}/v1/reports/${videoId}` : undefined;
    });
  /** The case's live report, `null` while it loads, when it has none or when the read failed. */
  readonly liveReport = computed<LiveReport | null>(() =>
    this.reportResource.hasValue() ? (this.reportResource.value() ?? null) : null,
  );
  readonly reportAvailable = computed(() => !this.liveInProgress() && this.liveReport() !== null);

  /* What the library showed of the case, until the case itself answers. */
  private readonly preview = signal<Video | undefined>(undefined);
  /** The case, or what was known of it before it loaded: enough to name it and draw its bar. */
  readonly known = computed(() => this.video() ?? this.preview());
  /* The library's copy first: already in the browser, where the case's is signed anew and fetched again. */
  /* Before any of it, what the tab last knew: after a reload, the bar must not offer the wrong action. */
  readonly publicationStatus = computed(
    () =>
      this.known()?.publicationStatus ??
      this.openCases.opened().find((held) => held.id === this.id)?.publicationStatus,
  );
  readonly picture = computed(() => this.preview()?.thumbnailUrl ?? this.video()?.thumbnailUrl);

  private readonly cutsLoading = signal(false);
  private readonly conversation = signal<Conversation | undefined>(undefined);
  /** Whether its chat can talk yet: before that, there is no conversation to wait for. */
  private readonly chatReady = computed(
    () => this.video()?.chatStatus === AvailabilityStatus.Available,
  );
  /**
   * Until the case can be read properly: the case, its contents, its findings when it has some, and
   * its chat's conversation. Once loaded, coming back to its tab waits for nothing.
   */
  readonly loading = computed(
    () =>
      this.videoResource.isLoading() ||
      this.cutsLoading() ||
      (this.piiToReview() && !this.piiLoaded()) ||
      (this.chatReady() && (this.conversation()?.loading() ?? true)),
  );

  // Set in the constructor: a field initialiser runs before the `id` parameter is assigned.
  readonly zones: ZoneRow<CaseZone, TabsState>;
  readonly contentDetailDialogOpen = signal(false);

  private readonly _piiList = signal<Pii[]>([]);
  private readonly piiLoaded = signal(false);
  /**
   * Findings to review before publishing. The case record counts them, so this is known with the
   * case itself — the zones are only drawn once the case is there, so the layout never moves.
   */
  readonly piiToReview = computed(() => {
    const video = this.video();
    return (
      !!video &&
      video.stats.piiCount > 0 &&
      this.permissions.canPublishCase() &&
      video.publicationStatus === PublicationStatus.Private
    );
  });
  readonly piiDetected = computed(() => this.piiToReview() || this._piiList().length > 0);
  readonly piiList = computed(() => this._piiList());
  /** Whether the findings are on screen. Their own block, opened and closed from the banner. */
  readonly phiPanelOpen = signal(false);

  /**
   * The AI content zone holds the PHI findings as well as the generated cuts, so it exists for a
   * user who can only see the findings — otherwise a PHI case would open on an empty zone.
   */
  readonly contentZoneAvailable = computed(
    () => this.permissions.canSeeAndGenerateCut() || this.piiDetected(),
  );

  private readonly _videoCuts = signal<VideoCut[]>([]);
  readonly videoCuts = computed(() =>
    this._videoCuts().sort(
      (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
    ),
  );
  readonly contentCounts = computed(() =>
    this.videoCuts().reduce(
      (counts, item) => {
        if (item.status === 'Failed') {
          counts.errorCount += 1;
        } else if (item.status === 'Completed') {
          counts.successCount += 1;
        } else if (item.status === 'InProgress') {
          counts.progressCount += 1;
        }
        return counts;
      },
      { successCount: 0, progressCount: 0, errorCount: 0 },
    ),
  );

  private readonly _timeClicked = signal<string>('');
  readonly timeInSeconds = computed<number | undefined>(() => {
    if (!this._timeClicked()) {
      return;
    }
    return timeToDuration(this._timeClicked());
  });
  private readonly _videoPaused = signal<boolean | null>(null);
  readonly videoPaused = this._videoPaused.asReadonly();

  constructor(readonly id: string) {
    this.zones = new ZoneRow(
      inject(MibBreakpointService).sizeClass,
      caseZoneRules(this.reportAvailable),
      {
        first: 'video',
        defaults: DEFAULT_ZONES,
        // A guest shares the browser of a signed-in user: their stored layout is not the guest's.
        storage: this.users.isGuest()
          ? null
          : { key: STORAGE_KEYS.CASE_ZONES, id, kept: REMEMBERED_ENTRIES },
      },
    );
    /* The bar holds the tab, named again on every read: the title lands after the id. */
    effect(() => {
      const video = this.video();
      if (video) {
        this.openCases.open(this.id, video.title, aboutTab(video));
      }
    });

    effect(() => {
      // A chosen zone the case no longer offers — content on a case with neither findings nor
      // cuts — would show nothing at all.
      if (!this.contentZoneAvailable() && this.zones.single() === 'content') {
        this.zones.show('video');
      }
    });

    effect(() => {
      // Decided once, from the case alone: tracked, the layout read inside would put the findings
      // straight back on every zone the user picks afterwards.
      if (this.piiToReview()) {
        untracked(() => this.openPiiPanel());
      }
    });

    effect(() => {
      if (this.piiLoaded() || !this.piiToReview()) {
        return;
      }
      this.videoApi.getPiiList(this.id).subscribe({
        next: (piiList) => {
          this.piiLoaded.set(true);
          this.setPiiList(piiList);
        },
        error: (error) => {
          this.piiLoaded.set(true);
          console.error(`An error occurred while loading the PHI list:`, error);
        },
      });
    });

    effect(() => {
      if (this.contentCounts().progressCount > 0) {
        untracked(() => this.zones.open('content'));
      }
    });

    // Fetched with the case rather than when its chat is drawn, so the tab knows when it is ready.
    effect(() => {
      const video = this.video();
      if (video && this.chatReady() && !this.conversation()) {
        const chatId = this.users.isGuest() ? video.sharedId : video.id;
        untracked(() => this.conversation.set(this.conversations.prefetch(chatId)));
      }
    });

    if (this.permissions.canSeeAndGenerateCut()) {
      this.cutsLoading.set(true);
      this.contentService
        .listVideoCuts(this.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .pipe(finalize(() => this.cutsLoading.set(false)))
        .subscribe({
          next: (cuts) => this.setVideoCuts(cuts),
          error: (error) => console.error(`An error occurred while loading the video cuts:`, error),
        });
    }

    this.videoHub.start(this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.destroyRef.onDestroy(() => {
      this.videoHub.release(this.id);
      // Where the video stood belongs to the tab: closed, the case starts over next time.
      this.videoProgress.clearTime(this.id);
    });
  }

  /** Takes what the library already knew of the case, so its tab is named before it loads. */
  foresee(video: Video): void {
    this.preview.set(video);
    if (!this.video()) {
      this.openCases.open(this.id, video.title, aboutTab(video));
    }
  }

  reload(): void {
    this.videoResource.reload();
  }

  /**
   * Asks for a fresh token before the one the player holds expires.
   *
   * The previous value stays on while the request runs, so the player — which keys its connection
   * on the server and the room, never on the token — keeps streaming through it.
   */
  renewLive(): void {
    this.liveResource.reload();
  }

  applyLiveUpdate(pushed: Video): void {
    /*
     A push says nothing about the session — it never carries `isLive` — so a live case is read
     again instead, which is how the end of the live reaches a viewer whose player never joined.
    */
    if (this.liveInProgress()) {
      this.videoResource.reload();
      return;
    }
    // Only once it has its value: updating a resource still loading cancels its request.
    if (this.videoResource.hasValue()) {
      this.videoResource.update((current) => (current ? mergePush(current, pushed) : current));
    }
    // Not in yet: what the library knew follows, so the bar never acts on a stale state.
    this.preview.update((known) => (known ? mergePush(known, pushed) : known));
  }

  /**
   * The player saw the room close. LiveKit deletes it when the live is stopped, so the case is read
   * again: it comes back without `isLive`, and the recording takes the stream's place.
   */
  liveEnded(): void {
    this.videoResource.reload();
  }

  /**
   * Get a specific video cut by ID as a computed signal
   */
  getVideoCutById(cutId: string): Signal<VideoCut | undefined> {
    return computed(() => this._videoCuts().find((cut) => cut.cutId === cutId));
  }

  openPiiPanel(): void {
    this.phiPanelOpen.set(true);
  }

  closePhiPanel(): void {
    this.phiPanelOpen.set(false);
  }

  setVideoCuts(videoCuts: VideoCut[]): void {
    this._videoCuts.set(videoCuts);
  }

  setPiiList(piiList: PiiList): void {
    this._piiList.set(piiList.findings);
  }

  resetPiiList(): void {
    this._piiList.set([]);
  }

  /**
   * Add a new videoCut or edit an existing one.
   * @param newVideoCut The video cut to add or edit
   */
  updateVideoCuts(newVideoCut: VideoCut) {
    this._videoCuts.update((videoCuts) => {
      const existingCut = videoCuts.find((cut) => cut.cutId === newVideoCut.cutId);
      if (existingCut) {
        this.trackingGenerationEvents(newVideoCut, existingCut);
        return videoCuts.map((cut) => (cut.cutId === newVideoCut.cutId ? { ...newVideoCut } : cut));
      } else {
        return videoCuts.concat([newVideoCut]);
      }
    });
  }

  /**
   * Delete a video cut from the list
   * @param cutId Video cut identifier
   */
  deleteVideoCut(cutId: string) {
    this._videoCuts.update((cuts) => cuts.filter((cut) => cut.cutId !== cutId));
  }

  clickOnTimeLink(ts: string): void {
    this.zones.move();
    this.zones.show('video');
    this._timeClicked.set(ts);
    this._videoPaused.set(false);
  }

  /**
   * Reset the pending timestamp once the player has seeked to it, so that
   * re-clicking the same timestamp is seen as a new value and seeks again.
   */
  clearTimeClicked(): void {
    this._timeClicked.set('');
  }

  pauseVideo(): void {
    this._videoPaused.set(true);
  }

  /**
   * Tracking video edit generation events based on the difference between the status of the newVideoCut and the existingCut:
   * - if existingCut was in progress and newVideoCut is completed => generation succeeded
   * - if existingCut was in progress and newVideoCut is error => generation failed
   * @param newVideoCut video cut that arrived from web socket
   * @param existingCut video cut with same id that already existed on the state
   */
  private trackingGenerationEvents(newVideoCut: VideoCut, existingCut: VideoCut): void {
    const eventParams = {
      case_id: newVideoCut.videoId,
      video_edit_id: newVideoCut.cutId,
    };
    if (
      newVideoCut.status === ContentGenerationStatus.Completed &&
      existingCut.status === ContentGenerationStatus.Progress
    ) {
      this.analytics.trackEvent(AnalyticsEvents.VideoEditGenerated, {
        ...eventParams,
        video_edit_duration: newVideoCut.duration,
        video_edit_sequence_count: newVideoCut.segments.length,
      });
    } else if (
      newVideoCut.status === ContentGenerationStatus.Failed &&
      existingCut.status === ContentGenerationStatus.Progress
    ) {
      this.analytics.trackEvent(AnalyticsEvents.VideoEditError, eventParams);
    }
  }
}
